import { useState, useEffect } from 'react';
import {
  listRestaurantCategories,
  createRestaurantCategory,
  updateRestaurantCategory,
  deleteRestaurantCategory,
  toggleRestaurantCategory,
  reorderRestaurantCategories,
  type RestaurantCategory,
} from '@/shared/services/restaurantCategoriesService';
import { ConfirmModal } from '@/shared/components/ConfirmModal';
import styles from './RestaurantCategoriesPage.module.css';

export function RestaurantCategoriesPage() {
  const [categories, setCategories] = useState<RestaurantCategory[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Estados para modales
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [editingCategory, setEditingCategory] = useState<RestaurantCategory | null>(null);
  const [deletingCategory, setDeletingCategory] = useState<RestaurantCategory | null>(null);

  // Estados para formularios
  const [formName, setFormName] = useState('');
  const [formSlug, setFormSlug] = useState('');
  const [isSlugManuallyEdited, setIsSlugManuallyEdited] = useState(false);
  const [formIcon, setFormIcon] = useState('');
  const [processing, setProcessing] = useState(false);

  useEffect(() => {
    loadCategories();
  }, []);

  const loadCategories = async () => {
    try {
      setLoading(true);
      setError('');
      const categoriesData = await listRestaurantCategories();
      setCategories(categoriesData);
    } catch (err: any) {
      console.error('Error al cargar categorías:', err);
      setError(err?.response?.data?.message || err?.message || 'Error al cargar las categorías');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateCategory = async () => {
    if (processing) return;

    if (!formName.trim()) {
      setError('El nombre de la categoría es requerido');
      return;
    }

    try {
      setProcessing(true);
      setError('');

      await createRestaurantCategory({
        name: formName.trim(),
        slug: formSlug.trim() || undefined,
        icon: formIcon.trim() || undefined,
      });

      setSuccess('Categoría creada exitosamente');
      setShowCreateModal(false);
      resetForm();
      await loadCategories();
    } catch (err: any) {
      console.error('Error al crear categoría:', err);
      setError(err?.response?.data?.message || err?.message || 'Error al crear la categoría');
    } finally {
      setProcessing(false);
    }
  };

  const handleUpdateCategory = async () => {
    if (processing || !editingCategory) return;

    if (!formName.trim()) {
      setError('El nombre de la categoría es requerido');
      return;
    }

    try {
      setProcessing(true);
      setError('');

      await updateRestaurantCategory(editingCategory.categoryId, {
        name: formName.trim(),
        slug: formSlug.trim() || undefined,
        icon: formIcon.trim() || undefined,
      });

      setSuccess('Categoría actualizada exitosamente');
      setShowEditModal(false);
      setEditingCategory(null);
      resetForm();
      await loadCategories();
    } catch (err: any) {
      console.error('Error al actualizar categoría:', err);
      setError(err?.response?.data?.message || err?.message || 'Error al actualizar la categoría');
    } finally {
      setProcessing(false);
    }
  };

  const handleDeleteCategory = async () => {
    if (!deletingCategory || processing) return;

    try {
      setProcessing(true);
      setError('');
      await deleteRestaurantCategory(deletingCategory.categoryId);
      setSuccess('Categoría eliminada exitosamente');
      setShowDeleteModal(false);
      setDeletingCategory(null);
      await loadCategories();
    } catch (err: any) {
      console.error('Error al eliminar categoría:', err);
      setError(err?.response?.data?.message || err?.message || 'Error al eliminar la categoría');
    } finally {
      setProcessing(false);
    }
  };

  const handleToggleCategory = async (category: RestaurantCategory) => {
    if (processing) return;

    try {
      setProcessing(true);
      setError('');
      await toggleRestaurantCategory(category.categoryId);
      setSuccess(`Categoría ${!category.isActive ? 'activada' : 'desactivada'} exitosamente`);
      await loadCategories();
    } catch (err: any) {
      console.error('Error al cambiar estado de categoría:', err);
      setError(err?.response?.data?.message || err?.message || 'Error al cambiar el estado de la categoría');
    } finally {
      setProcessing(false);
    }
  };

  const handleMoveUp = async (index: number) => {
    if (index === 0 || processing) return;

    const newCategories = [...categories];
    const prevCategory = newCategories[index - 1];
    const currCategory = newCategories[index];
    if (prevCategory && currCategory) {
      newCategories[index - 1] = currCategory;
      newCategories[index] = prevCategory;
    }

    try {
      setProcessing(true);
      setError('');
      const categoryIds = newCategories.map((c) => c.categoryId);
      await reorderRestaurantCategories({ categoryIds });
      setSuccess('Categorías reordenadas exitosamente');
      await loadCategories();
    } catch (err: any) {
      console.error('Error al reordenar categorías:', err);
      setError(err?.response?.data?.message || err?.message || 'Error al reordenar las categorías');
    } finally {
      setProcessing(false);
    }
  };

  const handleMoveDown = async (index: number) => {
    if (index === categories.length - 1 || processing) return;

    const newCategories = [...categories];
    const nextCategory = newCategories[index + 1];
    const currCategory = newCategories[index];
    if (nextCategory && currCategory) {
      newCategories[index + 1] = currCategory;
      newCategories[index] = nextCategory;
    }

    try {
      setProcessing(true);
      setError('');
      const categoryIds = newCategories.map((c) => c.categoryId);
      await reorderRestaurantCategories({ categoryIds });
      setSuccess('Categorías reordenadas exitosamente');
      await loadCategories();
    } catch (err: any) {
      console.error('Error al reordenar categorías:', err);
      setError(err?.response?.data?.message || err?.message || 'Error al reordenar las categorías');
    } finally {
      setProcessing(false);
    }
  };

  const resetForm = () => {
    setFormName('');
    setFormSlug('');
    setFormIcon('');
    setIsSlugManuallyEdited(false);
  };

  const openEditModal = (category: RestaurantCategory) => {
    setEditingCategory(category);
    setFormName(category.name);
    setFormSlug(category.slug);
    setFormIcon(category.icon || '');
    setShowEditModal(true);
  };

  const openDeleteModal = (category: RestaurantCategory) => {
    setDeletingCategory(category);
    setShowDeleteModal(true);
  };

  // Generar slug automáticamente desde el nombre
  // Generar slug automáticamente desde el nombre
  const handleNameChange = (value: string) => {
    setFormName(value);
    // Si el slug no ha sido editado manualmente y no estamos editando (o el slug era igual al generado)
    if (!isSlugManuallyEdited) {
      const autoSlug = value
        .toLowerCase()
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-+|-+$/g, '');
      setFormSlug(autoSlug);
    }
  };

  if (loading && categories.length === 0) {
    return (
      <div className={styles.container}>
        <div className={styles.loading}>
          <div className={styles.spinner}></div>
          <p>Cargando categorías...</p>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <div>
          <h1 className={styles.title}>Categorías de Comercios</h1>
          <p className={styles.subtitle}>
            Gestiona las categorías disponibles para los comercios. Los comercios pueden seleccionar una categoría
            al registrarse.
          </p>
        </div>
        <button className={styles.addButton} onClick={() => setShowCreateModal(true)}>
          + Nueva Categoría
        </button>
      </div>

      {error && (
        <div className={styles.alertError} onClick={() => setError('')}>
          {error}
        </div>
      )}
      {success && (
        <div className={styles.alertSuccess} onClick={() => setSuccess('')}>
          {success}
        </div>
      )}

      {categories.length === 0 ? (
        <div className={styles.emptyState}>
          <div className={styles.emptyIcon}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M9 11l3 3L22 4" />
              <path d="M21 12v7a2 2 0 01-2 2H5a2 2 0 01-2-2V5a2 2 0 012-2h11" />
            </svg>
          </div>
          <h2>No hay categorías</h2>
          <p>Agrega tu primera categoría para que los comercios puedan seleccionarla</p>
          <button className={styles.addButton} onClick={() => setShowCreateModal(true)}>
            + Crear Primera Categoría
          </button>
        </div>
      ) : (
        <div className={styles.categoriesTable}>
          <table>
            <thead>
              <tr>
                <th>Orden</th>
                <th>Nombre</th>
                <th>Slug</th>
                <th>Icono</th>
                <th>Estado</th>
                <th>Acciones</th>
              </tr>
            </thead>
            <tbody>
              {categories.map((category, index) => (
                <tr key={category.categoryId}>
                  <td>
                    <div className={styles.orderControls}>
                      <button
                        className={styles.reorderButton}
                        onClick={() => handleMoveUp(index)}
                        disabled={index === 0 || processing}
                        title="Mover arriba"
                      >
                        ↑
                      </button>
                      <span className={styles.orderNumber}>{category.order}</span>
                      <button
                        className={styles.reorderButton}
                        onClick={() => handleMoveDown(index)}
                        disabled={index === categories.length - 1 || processing}
                        title="Mover abajo"
                      >
                        ↓
                      </button>
                    </div>
                  </td>
                  <td>
                    <strong>{category.name}</strong>
                  </td>
                  <td>
                    <code className={styles.slug}>{category.slug}</code>
                  </td>
                  <td>{category.icon || '-'}</td>
                  <td>
                    <span className={category.isActive ? styles.statusActive : styles.statusInactive}>
                      {category.isActive ? 'Activa' : 'Inactiva'}
                    </span>
                  </td>
                  <td>
                    <div className={styles.actionButtons}>
                      <button
                        className={styles.editButton}
                        onClick={() => openEditModal(category)}
                        disabled={processing}
                        title="Editar"
                      >
                        ✏️
                      </button>
                      <button
                        className={`${styles.toggleButton} ${category.isActive ? styles.toggleActive : styles.toggleInactive}`}
                        onClick={() => handleToggleCategory(category)}
                        disabled={processing}
                        title={category.isActive ? 'Desactivar' : 'Activar'}
                      >
                        {category.isActive ? '👁️' : '👁️‍🗨️'}
                      </button>
                      <button
                        className={styles.deleteButton}
                        onClick={() => openDeleteModal(category)}
                        disabled={processing}
                        title="Eliminar"
                      >
                        🗑️
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Modal de Crear */}
      {showCreateModal && (
        <div className={styles.modalOverlay} onClick={() => !processing && setShowCreateModal(false)}>
          <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
            <div className={styles.modalHeader}>
              <h2>Nueva Categoría</h2>
              <button
                className={styles.modalClose}
                onClick={() => !processing && (setShowCreateModal(false), resetForm())}
                disabled={processing}
              >
                ×
              </button>
            </div>
            <div className={styles.modalBody}>
              <div className={styles.formGroup}>
                <label className={styles.label}>
                  Nombre de la Categoría * <span className={styles.helpText}>(ej: "Pizzería", "Comida Rápida")</span>
                </label>
                <input
                  type="text"
                  className={styles.input}
                  value={formName}
                  onChange={(e) => handleNameChange(e.target.value)}
                  placeholder="Pizzería"
                  disabled={processing}
                />
              </div>

              <div className={styles.formGroup}>
                <label className={styles.label}>
                  Slug <span className={styles.helpText}>(se genera automáticamente, puedes editarlo)</span>
                </label>
                <input
                  type="text"
                  className={styles.input}
                  value={formSlug}
                  onChange={(e) => {
                    setFormSlug(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '-'));
                    setIsSlugManuallyEdited(true);
                  }}
                  placeholder="pizzeria"
                  disabled={processing}
                />
                <p className={styles.helpText}>Usado para URLs. Solo letras minúsculas, números y guiones.</p>
              </div>

              <div className={styles.formGroup}>
                <label className={styles.label}>Icono (Opcional)</label>
                <input
                  type="text"
                  className={styles.input}
                  value={formIcon}
                  onChange={(e) => setFormIcon(e.target.value)}
                  placeholder="pizza, restaurant, cafe..."
                  disabled={processing}
                />
                <p className={styles.helpText}>Nombre del icono para mostrar en la UI (opcional).</p>
              </div>
            </div>
            <div className={styles.modalFooter}>
              <button
                className={styles.modalCancel}
                onClick={() => !processing && (setShowCreateModal(false), resetForm())}
                disabled={processing}
              >
                Cancelar
              </button>
              <button
                className={styles.modalSubmit}
                onClick={handleCreateCategory}
                disabled={processing || !formName.trim()}
              >
                {processing ? 'Creando...' : 'Crear Categoría'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal de Editar */}
      {showEditModal && editingCategory && (
        <div className={styles.modalOverlay} onClick={() => !processing && setShowEditModal(false)}>
          <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
            <div className={styles.modalHeader}>
              <h2>Editar Categoría</h2>
              <button
                className={styles.modalClose}
                onClick={() => !processing && (setShowEditModal(false), setEditingCategory(null), resetForm())}
                disabled={processing}
              >
                ×
              </button>
            </div>
            <div className={styles.modalBody}>
              <div className={styles.formGroup}>
                <label className={styles.label}>Nombre de la Categoría *</label>
                <input
                  type="text"
                  className={styles.input}
                  value={formName}
                  onChange={(e) => handleNameChange(e.target.value)}
                  placeholder="Pizzería"
                  disabled={processing}
                />
              </div>

              <div className={styles.formGroup}>
                <label className={styles.label}>Slug</label>
                <input
                  type="text"
                  className={styles.input}
                  value={formSlug}
                  onChange={(e) => setFormSlug(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '-'))}
                  placeholder="pizzeria"
                  disabled={processing}
                />
                <p className={styles.helpText}>Usado para URLs. Solo letras minúsculas, números y guiones.</p>
              </div>

              <div className={styles.formGroup}>
                <label className={styles.label}>Icono (Opcional)</label>
                <input
                  type="text"
                  className={styles.input}
                  value={formIcon}
                  onChange={(e) => setFormIcon(e.target.value)}
                  placeholder="pizza, restaurant, cafe..."
                  disabled={processing}
                />
                <p className={styles.helpText}>Nombre del icono para mostrar en la UI (opcional).</p>
              </div>
            </div>
            <div className={styles.modalFooter}>
              <button
                className={styles.modalCancel}
                onClick={() => !processing && (setShowEditModal(false), setEditingCategory(null), resetForm())}
                disabled={processing}
              >
                Cancelar
              </button>
              <button
                className={styles.modalSubmit}
                onClick={handleUpdateCategory}
                disabled={processing || !formName.trim()}
              >
                {processing ? 'Actualizando...' : 'Actualizar Categoría'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal de Confirmación de Eliminación */}
      <ConfirmModal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        onConfirm={handleDeleteCategory}
        title="Eliminar Categoría"
        message={
          deletingCategory
            ? `¿Estás seguro de que deseas eliminar la categoría "${deletingCategory.name}"? Esta acción no se puede deshacer. Solo puedes eliminar categorías que no estén siendo usadas por ningún comercio.`
            : ''
        }
        confirmText="Eliminar"
        cancelText="Cancelar"
        confirmButtonVariant="danger"
        isLoading={processing}
      />
    </div>
  );
}

